import { Link } from "react-router"
import { useMemo, useState } from "react"
import { Plus } from "lucide-react"

import { useCreosAssets } from "@/api/hooks/creos"
import { useOfferVerticals } from "@/api/hooks/offers"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { usePageTitle } from "@/layouts/pageChrome"
import { VERTICAL_PRESETS } from "@/lib/verticalCatalog"
import { cn } from "@/lib/utils"

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "generating", label: "Generating" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
] as const

function statusBadgeClass(status: string): string {
  switch (status) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-300"
    case "failed":
      return "bg-red-500/20 text-red-300"
    case "generating":
      return "bg-amber-500/20 text-amber-300"
    case "pending":
      return "bg-sky-500/20 text-sky-300"
    default:
      return "bg-white/10 text-[#aaa]"
  }
}

export default function CreosListPage() {
  usePageTitle("", "CreOS")
  const [status, setStatus] = useState("all")
  const [verticalSlug, setVerticalSlug] = useState("all")
  const [offset, setOffset] = useState(0)
  const pageSize = 50

  const params = useMemo(
    () => ({
      status: status === "all" ? undefined : status,
      vertical_slug: verticalSlug === "all" ? undefined : verticalSlug,
      limit: pageSize,
      offset,
    }),
    [status, verticalSlug, offset],
  )

  const { data, isLoading, error } = useCreosAssets(params, {
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? []
      const busy = items.some(
        (a) => a.status === "pending" || a.status === "generating",
      )
      return busy ? 4000 : false
    },
  })

  const total = data?.total ?? 0
  const canPrev = offset > 0
  const canNext = offset + pageSize < total

  const verticalsQuery = useOfferVerticals()
  const verticalOptions = useMemo(() => {
    const fromApi = verticalsQuery.data?.data ?? []
    const map = new Map<string, string>()
    for (const v of VERTICAL_PRESETS) map.set(v.slug, v.name)
    for (const v of fromApi) {
      if (v.slug) map.set(v.slug, v.name || v.slug)
    }
    return [...map.entries()]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [verticalsQuery.data])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">Generate</h1>
          <p className="mt-1 text-sm text-[#aaa]">
            AI ad image &amp; carousel generation
          </p>
        </div>
        <Button asChild className="bg-[#7488e8] hover:bg-[#687bda]">
          <Link to="/creos/new">
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            New request
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v)
            setOffset(0)
          }}
        >
          <SelectTrigger className="w-[180px] border-[#555] bg-[#2d2d2d] text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={verticalSlug}
          onValueChange={(v) => {
            setVerticalSlug(v)
            setOffset(0)
          }}
        >
          <SelectTrigger className="w-[220px] border-[#555] bg-[#2d2d2d] text-white">
            <SelectValue placeholder="Vertical" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All verticals</SelectItem>
            {verticalOptions.map((v) => (
              <SelectItem key={v.slug} value={v.slug}>
                {v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#4a4a4a] bg-[#2d2d2d]">
        {isLoading ? (
          <p className="p-6 text-sm text-[#aaa]">Loading requests…</p>
        ) : error ? (
          <p className="p-6 text-sm text-red-300">
            Failed to load requests: {error.message}
          </p>
        ) : !data?.items.length ? (
          <p className="p-6 text-sm text-[#aaa]">
            No generation requests yet. Create one to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-[#4a4a4a] bg-[#3a3a3a] text-[#aaa]">
                <tr>
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Vertical</th>
                  <th className="px-4 py-3 font-medium">Lang</th>
                  <th className="px-4 py-3 font-medium">Slides</th>
                  <th className="px-4 py-3 font-medium">Aspect</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((asset) => (
                  <tr
                    key={asset.id}
                    className="border-b border-[#3a3a3a] hover:bg-[#333]"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/creos/${asset.id}`}
                        className="font-medium text-[#8b9aff] hover:underline"
                      >
                        #{asset.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                          statusBadgeClass(asset.status),
                        )}
                      >
                        {asset.status}
                      </span>
                      {asset.error_message ? (
                        <p className="mt-1 max-w-xs truncate text-xs text-red-300">
                          {asset.error_message}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-white">
                      {asset.vertical_name}
                    </td>
                    <td className="px-4 py-3 uppercase text-[#ccc]">
                      {asset.language}
                    </td>
                    <td className="px-4 py-3 text-[#ccc]">
                      {asset.slide_count}{" "}
                      <span className="text-[#888]">
                        ({asset.asset_type})
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#ccc]">
                      {asset.aspect_ratio}
                    </td>
                    <td className="px-4 py-3 text-[#aaa]">
                      {asset.created_at
                        ? new Date(asset.created_at).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > pageSize ? (
        <div className="flex items-center justify-between gap-3 text-sm text-[#aaa]">
          <span>
            Showing {offset + 1}–{Math.min(offset + pageSize, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!canPrev}
              className="border-[#555] bg-transparent text-white hover:bg-[#333]"
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!canNext}
              className="border-[#555] bg-transparent text-white hover:bg-[#333]"
              onClick={() => setOffset(offset + pageSize)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
